/**
 * Applies a white-label business's primary colour to the glass design system.
 *
 * Rather than painting a flat colour onto the topbar, this drives the CSS
 * custom properties the theme is built on (see assets/glass/_tokens.scss).
 * Every brand-tinted surface in the app — topbar, active nav pills, primary
 * buttons, focus rings, table headers, scrollbars — reads from these vars, so
 * one call re-skins the whole shell while keeping the frosted-glass treatment
 * intact.
 */

const HEX_SHORT = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
const HEX_LONG = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

/** Parses #abc / #aabbcc / rgb(r,g,b) into [r, g, b]; null if unparseable. */
const toRgb = (color) => {
    if (typeof color !== 'string') return null;
    const value = color.trim();

    const short = value.match(HEX_SHORT);
    if (short) {
        return [short[1], short[2], short[3]].map((c) => parseInt(c + c, 16));
    }

    const long = value.match(HEX_LONG);
    if (long) {
        return [long[1], long[2], long[3]].map((c) => parseInt(c, 16));
    }

    const rgb = value.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
    if (rgb) {
        return [rgb[1], rgb[2], rgb[3]].map(Number);
    }

    return null;
};

/** Mixes a channel toward white (amount > 0) or black (amount < 0). */
const shade = ([r, g, b], amount) => {
    const target = amount > 0 ? 255 : 0;
    const t = Math.abs(amount);
    const mix = (c) => Math.round(c + (target - c) * t);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

export const applyBusinessTheme = (primaryColor) => {
    if (!primaryColor) return;

    const rgb = toRgb(primaryColor);
    const root = document.documentElement;

    root.style.setProperty('--brand-primary', primaryColor);

    if (rgb) {
        // The rgb triplet is what lets the stylesheet build brand-tinted
        // rgba() surfaces at arbitrary opacities from a single source colour.
        root.style.setProperty('--brand-rgb', rgb.join(', '));
        root.style.setProperty('--brand-primary-soft', shade(rgb, 0.25));
        root.style.setProperty('--brand-primary-strong', shade(rgb, -0.25));
        root.style.setProperty('--brand-accent', shade(rgb, 0.18));
    }

    // The detached-layout header band still comes from a stylesheet rule.
    let styleTag = document.getElementById('business-theme-style');
    if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'business-theme-style';
        document.head.appendChild(styleTag);
    }
    // The decorative band this paints only makes sense alongside the
    // absolute-positioned topbar it sits behind (see _layouts.scss,
    // body[data-layout="detached"] @include media-breakpoint-up(lg)) —
    // below that the topbar goes back to position: fixed and this band
    // shows through as an unrelated colour strip behind the page title.
    styleTag.innerHTML = `
        @media (min-width: 992px) {
            body[data-layout="detached"] #layout-wrapper::before {
                background: ${primaryColor} !important;
            }
        }
    `;

    // Clear the inline background a previous version of this helper may have
    // set — it carried !important and would override the glass topbar.
    const topbar = document.getElementById('page-topbar');
    if (topbar) {
        topbar.style.removeProperty('background');
    }
};
